import theme from '../../../theme';
import { getStatusChipPropsForRole } from '../../../config/statusDisplayResolver';
import { DEFICIENCY_STATUSES } from '../../../config/deficiencyStatusDefinitions';

/**
 * Canonical deficiency statuses are defined in config so display resolvers and
 * page constants can share them without a circular import.
 */
export { DEFICIENCY_STATUSES };

/**
 * Map UI status display values to state machine status values
 * This helps handle the case conversion and formatting differences
 * between the UI display status and the state machine status names from the backend
 */
export const UI_STATUS_TO_STATE_MAP = {
    submitted: 'submitted',
    accepted: 'accepted',
    assigned: 'assigned',
    'work completed': 'work_complete',
    'internally signed off': 'internally_signed_off',
    completed: 'completed',
    rejected: 'rejected',
};

/**
 * Converts a frontend display status value to the backend API enum value
 *
 * @param {string} displayStatus - Frontend display status (e.g., "Work Completed")
 * @returns {string} Backend API enum value (e.g., "WORK_COMPLETED")
 */
export const getApiStatusValue = (displayStatus) => {
    const statusEntry = Object.values(DEFICIENCY_STATUSES).find(
        (status) => status.value === displayStatus
    );

    return statusEntry ? statusEntry.apiValue : displayStatus;
};

/**
 * Converts a backend API enum status value to the frontend display value
 *
 * @param {string} apiStatus - Backend API enum status (e.g., "WORK_COMPLETED")
 * @returns {string} Frontend display value (e.g., "Work Completed")
 */
export const getDisplayStatusValue = (apiStatus) => {
    const statusEntry = Object.values(DEFICIENCY_STATUSES).find(
        (status) => status.apiValue === apiStatus
    );

    return statusEntry ? statusEntry.value : apiStatus;
};

/**
 * Helper function to get chip props for a given status
 * Provides consistent styling for status chips throughout the application
 *
 * @param {string} status - The status value to get props for
 * @returns {Object} Props for the MUI Chip component
 */
export const getStatusChipProps = (status, role) => {
    if (role) {
        return getStatusChipPropsForRole(status, role);
    }

    const statusObj = Object.values(DEFICIENCY_STATUSES).find(
        (s) => s.value === status || s.apiValue === status
    );
    if (!statusObj) {
        return {
            label: status || 'Unknown',
            pendingDescription: null,
            sx: {
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.grey[600],
            },
        };
    }

    return {
        label: statusObj.label,
        pendingDescription: statusObj.pendingDescription,
        sx: {
            backgroundColor: `${statusObj.color}15`,
            color: statusObj.color,
            fontWeight: 500,
        },
    };
};

/**
 * Action type constants for state transitions
 * These define all possible transitions between deficiency states
 */
export const TRANSITION_ACTIONS = {
    TRANSITION_1A_ACCEPT: 'TRANSITION_1A_ACCEPT',
    TRANSITION_1B_ASSIGN_FROM_SUBMITTED: 'TRANSITION_1B_ASSIGN_FROM_SUBMITTED',
    TRANSITION_1C_FINISH_FROM_SUBMITTED: 'TRANSITION_1C_FINISH_FROM_SUBMITTED',
    TRANSITION_1D_REJECT_SUBMITTED: 'TRANSITION_1D_REJECT_SUBMITTED',
    TRANSITION_2A_ASSIGN_FROM_ACCEPTED: 'TRANSITION_2A_ASSIGN_FROM_ACCEPTED',
    TRANSITION_2B_FINISH_FROM_ACCEPTED: 'TRANSITION_2B_FINISH_FROM_ACCEPTED',
    TRANSITION_3A_FINISH_FROM_ASSIGNED: 'TRANSITION_3A_FINISH_FROM_ASSIGNED',
    TRANSITION_4A_SIGNOFF_INTERNAL: 'TRANSITION_4A_SIGNOFF_INTERNAL',
    TRANSITION_4B_COMPLETE_INTERNAL: 'TRANSITION_4B_COMPLETE_INTERNAL',
    TRANSITION_5A_COMPLETE_EXTERNAL: 'TRANSITION_5A_COMPLETE_EXTERNAL',
    TRANSITION_4C_REJECT_TO_ASSIGNED: 'TRANSITION_4C_REJECT_TO_ASSIGNED',
    // External reviewer bounces work back from INTERNALLY_SIGNED_OFF → ASSIGNED
    TRANSITION_5B_REJECT_EXTERNAL: 'TRANSITION_5B_REJECT_EXTERNAL',
};

/**
 * Configuration for state transitions
 * Defines allowed transitions, source/target states, and role requirements
 */
export const STATE_TRANSITIONS = {
    [TRANSITION_ACTIONS.TRANSITION_1A_ACCEPT]: {
        from: DEFICIENCY_STATUSES.SUBMITTED.value,
        to: DEFICIENCY_STATUSES.ACCEPTED.value,
        allowedRoles: ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'],
    },
    [TRANSITION_ACTIONS.TRANSITION_1B_ASSIGN_FROM_SUBMITTED]: {
        from: DEFICIENCY_STATUSES.SUBMITTED.value,
        to: DEFICIENCY_STATUSES.ASSIGNED.value,
        allowedRoles: ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'],
    },
    [TRANSITION_ACTIONS.TRANSITION_1C_FINISH_FROM_SUBMITTED]: {
        from: DEFICIENCY_STATUSES.SUBMITTED.value,
        to: DEFICIENCY_STATUSES.WORK_COMPLETED.value,
        allowedRoles: ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'],
    },
    [TRANSITION_ACTIONS.TRANSITION_1D_REJECT_SUBMITTED]: {
        from: DEFICIENCY_STATUSES.SUBMITTED.value,
        to: DEFICIENCY_STATUSES.REJECTED.value,
        allowedRoles: ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'],
    },
    [TRANSITION_ACTIONS.TRANSITION_2A_ASSIGN_FROM_ACCEPTED]: {
        from: DEFICIENCY_STATUSES.ACCEPTED.value,
        to: DEFICIENCY_STATUSES.ASSIGNED.value,
        allowedRoles: ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'],
    },
    [TRANSITION_ACTIONS.TRANSITION_2B_FINISH_FROM_ACCEPTED]: {
        from: DEFICIENCY_STATUSES.ACCEPTED.value,
        to: DEFICIENCY_STATUSES.WORK_COMPLETED.value,
        allowedRoles: ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'],
    },
    [TRANSITION_ACTIONS.TRANSITION_3A_FINISH_FROM_ASSIGNED]: {
        from: DEFICIENCY_STATUSES.ASSIGNED.value,
        to: DEFICIENCY_STATUSES.WORK_COMPLETED.value,
        allowedRoles: [
            'INTERNAL_PM',
            'INTERNAL_SUPER',
            'INTERNAL_TRADE',
            'EXTERNAL_SUBCONTRACTOR_TRADE',
            'EXTERNAL_SUBCONTRACTOR_PM',
            'INTERNAL_ADMIN',
        ],
    },
    [TRANSITION_ACTIONS.TRANSITION_4A_SIGNOFF_INTERNAL]: {
        from: DEFICIENCY_STATUSES.WORK_COMPLETED.value,
        to: DEFICIENCY_STATUSES.INTERNALLY_SIGNED_OFF.value,
        allowedRoles: ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'],
    },
    [TRANSITION_ACTIONS.TRANSITION_4B_COMPLETE_INTERNAL]: {
        from: DEFICIENCY_STATUSES.WORK_COMPLETED.value,
        to: DEFICIENCY_STATUSES.COMPLETED.value,
        allowedRoles: ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'],
        requiresInternallyAdded: true,
    },
    [TRANSITION_ACTIONS.TRANSITION_5A_COMPLETE_EXTERNAL]: {
        from: DEFICIENCY_STATUSES.INTERNALLY_SIGNED_OFF.value,
        to: DEFICIENCY_STATUSES.COMPLETED.value,
        allowedRoles: ['EXTERNAL_OWNER', 'EXTERNAL_CONSULTANT'],
    },
    [TRANSITION_ACTIONS.TRANSITION_4C_REJECT_TO_ASSIGNED]: {
        from: DEFICIENCY_STATUSES.WORK_COMPLETED.value,
        to: DEFICIENCY_STATUSES.ASSIGNED.value,
        allowedRoles: ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'],
    },
    // External reviewer rejects from INTERNALLY_SIGNED_OFF (bounce-back to ASSIGNED)
    [TRANSITION_ACTIONS.TRANSITION_5B_REJECT_EXTERNAL]: {
        from: DEFICIENCY_STATUSES.INTERNALLY_SIGNED_OFF.value,
        to: DEFICIENCY_STATUSES.ASSIGNED.value,
        allowedRoles: ['EXTERNAL_OWNER', 'EXTERNAL_CONSULTANT'],
    },
};

/**
 * Helper function to determine if a transition is allowed
 *
 * @param {string} currentStatus - Current status of the deficiency
 * @param {string} actionType - The transition action type to check
 * @param {string} userRole - Role of the user attempting the transition
 * @param {boolean} internallyCreated - Whether the deficiency was internally created
 * @returns {boolean} Whether the transition is allowed
 */
export const canTransition = (currentStatus, actionType, userRole, internallyCreated = false) => {
    // Get the transition config for this action
    const transitionConfig = STATE_TRANSITIONS[actionType];

    // If no config exists for this action, transition is not allowed
    if (!transitionConfig) {
        return false;
    }

    // Check if current status matches the required 'from' status
    if (transitionConfig.from !== currentStatus) {
        return false;
    }

    // Check if user has required role
    if (!transitionConfig.allowedRoles.includes(userRole)) {
        return false;
    }

    // Check if transition requires internallyAdded flag
    if (transitionConfig.requiresInternallyAdded && !internallyCreated) {
        return false;
    }

    return true;
};

/**
 * Given a status, action, and user role, returns the correct transition action
 *
 * @param {string} currentStatus - The current status of the deficiency
 * @param {string} action - The action being taken (accept, assign, finish, etc.)
 * @param {string} userRole - The role of the current user
 * @param {boolean} internallyCreated - Whether the deficiency was internally created
 * @returns {string|null} The correct transition action or null if no valid transition
 */
export const getCorrectTransitionAction = (
    currentStatus,
    action,
    userRole,
    internallyCreated = false
) => {
    // Normalize status to handle case differences
    const normalizedStatus = currentStatus?.toLowerCase() || '';

    // Convert UI status values to state machine status values
    // E.g., "Work Completed" -> "work_complete"
    const mappedStatus = UI_STATUS_TO_STATE_MAP[normalizedStatus] || normalizedStatus;

    // Map the basic action (accept, assign, etc.) to specific transitions based on current status
    if (
        action === 'accept' &&
        mappedStatus === 'submitted' &&
        (userRole === 'INTERNAL_PM' ||
            userRole === 'INTERNAL_SUPER' ||
            userRole === 'INTERNAL_ADMIN')
    ) {
        return TRANSITION_ACTIONS.TRANSITION_1A_ACCEPT;
    }

    if (action === 'assign') {
        if (mappedStatus === 'submitted') {
            return TRANSITION_ACTIONS.TRANSITION_1B_ASSIGN_FROM_SUBMITTED;
        }
        if (mappedStatus === 'accepted') {
            return TRANSITION_ACTIONS.TRANSITION_2A_ASSIGN_FROM_ACCEPTED;
        }
    }

    if (action === 'finish') {
        if (
            mappedStatus === 'submitted' &&
            (userRole === 'INTERNAL_PM' ||
                userRole === 'INTERNAL_SUPER' ||
                userRole === 'INTERNAL_ADMIN')
        ) {
            return TRANSITION_ACTIONS.TRANSITION_1C_FINISH_FROM_SUBMITTED;
        }
        if (
            mappedStatus === 'accepted' &&
            (userRole === 'INTERNAL_PM' ||
                userRole === 'INTERNAL_SUPER' ||
                userRole === 'INTERNAL_ADMIN')
        ) {
            return TRANSITION_ACTIONS.TRANSITION_2B_FINISH_FROM_ACCEPTED;
        }
        if (mappedStatus === 'assigned') {
            return TRANSITION_ACTIONS.TRANSITION_3A_FINISH_FROM_ASSIGNED;
        }
    }

    if (action === 'approve') {
        if (mappedStatus === 'work_complete') {
            if (
                internallyCreated &&
                (userRole === 'INTERNAL_PM' ||
                    userRole === 'INTERNAL_SUPER' ||
                    userRole === 'INTERNAL_ADMIN')
            ) {
                return TRANSITION_ACTIONS.TRANSITION_4B_COMPLETE_INTERNAL;
            } else if (
                userRole === 'INTERNAL_PM' ||
                userRole === 'INTERNAL_SUPER' ||
                userRole === 'INTERNAL_ADMIN'
            ) {
                return TRANSITION_ACTIONS.TRANSITION_4A_SIGNOFF_INTERNAL;
            }
        }
        if (
            mappedStatus === 'internally_signed_off' &&
            (userRole === 'EXTERNAL_OWNER' || userRole === 'EXTERNAL_CONSULTANT')
        ) {
            return TRANSITION_ACTIONS.TRANSITION_5A_COMPLETE_EXTERNAL;
        }
    }

    // Terminal rejection from SUBMITTED (quick-reject)
    if (action === 'reject_terminal') {
        if (
            mappedStatus === 'submitted' &&
            (userRole === 'INTERNAL_PM' ||
                userRole === 'INTERNAL_SUPER' ||
                userRole === 'INTERNAL_ADMIN')
        ) {
            return TRANSITION_ACTIONS.TRANSITION_1D_REJECT_SUBMITTED;
        }
    }

    // Work rejection/bounce-back from WORK_COMPLETED (internal PM review)
    if (action === 'reject_work') {
        if (
            mappedStatus === 'work_complete' &&
            (userRole === 'INTERNAL_PM' ||
                userRole === 'INTERNAL_SUPER' ||
                userRole === 'INTERNAL_ADMIN')
        ) {
            return TRANSITION_ACTIONS.TRANSITION_4C_REJECT_TO_ASSIGNED;
        }
        // Work rejection/bounce-back from INTERNALLY_SIGNED_OFF (external final review)
        if (
            mappedStatus === 'internally_signed_off' &&
            (userRole === 'EXTERNAL_OWNER' || userRole === 'EXTERNAL_CONSULTANT')
        ) {
            return TRANSITION_ACTIONS.TRANSITION_5B_REJECT_EXTERNAL;
        }
    }

    // If no valid transition is found, return null
    return null;
};
